import { formatNumber, guardNumber } from "../numbers";
import { guardString } from "../strings";
import type { Nullable, Tables } from "../types";

type CalculationResultItem = Pick<
	Tables<"serviceCalculationItems">,
	"type" | "unit" | "quantity" | "unitPrice" | "total"
>;

/**
 * Builds a short human-readable summary of a calculation item's result: a block's
 * repeat count, or a service's total measurement (piece count, running meters, area or
 * volume). A service's measurement is derived from its stored `total`/`unitPrice`
 * rather than raw dimension fields, since its actual dimensions live on its named
 * positions rather than on the item itself.
 *
 * @param item - The calculation item row to summarize.
 * @returns A short unit-suffixed result string, or "--" when not derivable.
 */
export function getCalculationResultString(item: Nullable<CalculationResultItem>) {
	if (!item) return guardString(null);
	if (item.type === "block") return `× ${formatNumber(item.quantity, "decimal")}`;

	const measurement =
		guardNumber(item.unitPrice) > 0 ? guardNumber(item.total) / guardNumber(item.unitPrice) : 0;

	switch (item.unit) {
		case "piece":
			return `${formatNumber(measurement, "decimal")} Stk.`;
		case "piece-week":
			return `${formatNumber(measurement, "decimal")} Stk./Wo.`;
		case "meters":
			return `${formatNumber(measurement, "decimal")} m`;
		case "meters-week":
			return `${formatNumber(measurement, "decimal")} m/Wo.`;
		case "square-meters":
			return `${formatNumber(measurement, "decimal")} m²`;
		case "square-meters-week":
			return `${formatNumber(measurement, "decimal")} m²/Wo.`;
		case "cubic-meters":
			return `${formatNumber(measurement, "decimal")} m³`;
		default:
			return guardString(null);
	}
}
